import { computed, onScopeDispose, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import { liveQuery } from 'dexie'
import type { Category, Comic, ComicCategory } from '@tanko/shared'
import { COVER_THUMBNAIL_WIDTH } from '@tanko/shared'
import { db, initializeDatabase, type Library, type StoredProgress } from '../db/schema'
import { addComics, relinkComic as relinkComicRow, relocateLibrary, removeComic, removeLibrary } from '../db/repo'
import { errorMessage, imageFileNames, listCandidates, type CandidateScan } from '../lib/fs-access'
import { findSameSource, libraryForComic, resolveComicArchive, resolveComicDirectory } from '../lib/library-sources'
import type { IndexJob, IndexResult } from '../workers/indexer.worker'
import { filterComics } from '../lib/comic-filter'
import { sortComics } from '../lib/comic-sort'
import { clearSelection, countWithin, pruneSelection, selectAll, selectRange, toggleSelection } from '../lib/comic-selection'
import { useLibraryDisplay } from '../lib/library-preferences'
import { useCatalogStore } from './catalog'

/** 來源重新指向的預覽：新 handle 底下找得到幾本，確認前給使用者看。 */
export interface RelocationPreview {
  libraryId: string
  handle: FileSystemDirectoryHandle
  found: number
  total: number
  missingTitles: string[]
}

export const useLibraryStore = defineStore('library', () => {
  const catalog = useCatalogStore()
  const libraries = shallowRef<Library[]>([])
  const comics = ref<Comic[]>([])
  const categories = ref<Category[]>([])
  const assignments = ref<ComicCategory[]>([])
  const ready = ref(false)
  // 授權綁在個別 handle 上，因此以來源 id 為鍵各自保存。
  const authorizedIds = ref(new Set<string>())
  const busy = ref(false)
  const refreshing = ref(false)
  const error = ref('')
  const missing = ref(new Set<string>())
  const indexing = ref(new Set<string>())
  const indexErrors = ref<Record<string, string>>({})
  const progress = ref(new Map<string, StoredProgress>())
  const query = ref('')
  const selectedCategories = ref<string[]>([])
  const selectionMode = ref(false)
  const selection = ref<ReadonlySet<string>>(new Set())
  /** shift 範圍選取的錨點：上一次單獨點選的那一本。 */
  const selectionAnchor = ref<string>()
  const supported = typeof window.showDirectoryPicker === 'function'
  let initialized = false
  let subscription: { unsubscribe(): void } | undefined
  let worker: Worker | undefined
  let activeId: string | undefined
  const queue: IndexJob[] = []

  const roots = computed(() => libraries.value.filter(library => library.kind === 'root'))
  const hasSources = computed(() => libraries.value.length > 0)
  const unauthorizedSources = computed(() => libraries.value.filter(library => !authorizedIds.value.has(library.id)))
  const authorized = computed(() => hasSources.value && !unauthorizedSources.value.length)
  function isAuthorized(libraryId: string): boolean { return authorizedIds.value.has(libraryId) }
  function sourceOf(comic: Pick<Comic, 'libraryId'>): Library | undefined {
    return libraryForComic(libraries.value, comic.libraryId)
  }
  function comicsInSource(libraryId: string): Comic[] {
    return comics.value.filter(comic => comic.libraryId === libraryId)
  }

  function comicsForCategories(categories: readonly string[]) {
    return filterComics(comics.value, assignments.value, categories, query.value)
  }
  // 排序只套在顯示清單這一步；資料層的讀取順序不動，標題被編輯後排序即時反映。
  const display = useLibraryDisplay()
  const filteredComics = computed(() => sortComics(comicsForCategories(selectedCategories.value), display.sortOrder.value))

  const selectedCount = computed(() => selection.value.size)
  /** 選取中有多少本落在目前的篩選結果內。與總數不同時兩個數字都要呈現。 */
  const selectedInFilter = computed(() => countWithin(selection.value, filteredComics.value.map(comic => comic.id)))
  const selectedComics = computed(() => comics.value.filter(comic => selection.value.has(comic.id)))
  function enterSelectionMode() { selectionMode.value = true }
  function exitSelectionMode() {
    selectionMode.value = false
    selection.value = clearSelection()
    selectionAnchor.value = undefined
  }
  /** `extend` 為 shift 點擊：從上一次單獨點選的那一本連選到這一本。 */
  function toggleSelected(id: string, extend = false) {
    const anchor = selectionAnchor.value
    if (extend && anchor && anchor !== id) {
      selection.value = selectRange(selection.value, filteredComics.value.map(comic => comic.id), anchor, id)
      return
    }
    selection.value = toggleSelection(selection.value, id)
    selectionAnchor.value = id
  }
  /** 全選只加入目前篩選結果，不移除落在篩選之外的既有選取。 */
  function selectAllFiltered() {
    selection.value = selectAll(selection.value, filteredComics.value.map(comic => comic.id))
  }
  function clearSelected() { selection.value = clearSelection(); selectionAnchor.value = undefined }
  function dropFromSelection(ids: readonly string[]) {
    const removed = new Set(ids)
    selection.value = new Set([...selection.value].filter(id => !removed.has(id)))
  }

  async function loadLibraries() {
    libraries.value = await db.libraries.orderBy('addedAt').toArray()
  }

  /**
   * 還原授權狀態。queryPermission 只回報目前的權限，不會彈出提示也不需要使用者手勢，
   * 因此可以在載入流程中呼叫——這與「requestPermission 必須位於手勢呼叫堆疊內」
   * 是兩件不同的事。查不到或丟例外一律當成未授權。
   */
  async function restorePermissions() {
    const granted = new Set<string>()
    for (const library of libraries.value) {
      try {
        if (await library.handle.queryPermission?.({ mode: 'read' }) === 'granted') granted.add(library.id)
      } catch { /* handle 失效或瀏覽器不支援，視為未授權。 */ }
    }
    authorizedIds.value = granted
    if (granted.size) await refresh()
  }

  async function initialize() {
    if (initialized) return
    initialized = true
    try {
      await initializeDatabase()
      await loadLibraries()
      const load = async () => ({
        comics: await db.comics.orderBy('addedAt').reverse().toArray(),
        categories: await db.categories.orderBy('sortOrder').toArray(),
        assignments: await db.comicCategories.toArray(),
        // 卡片進度條與已存在的訂閱共用同一次查詢，不另開一條 liveQuery。
        progress: await db.readingProgress.toArray(),
      })
      subscription = liveQuery(load).subscribe({
        next(data) {
          comics.value = data.comics
          // 清單一更新就修剪選取，讓「已選取 N 本」不可能指到不存在的作品。
          selection.value = pruneSelection(selection.value, new Set(data.comics.map(comic => comic.id)))
          categories.value = data.categories
          assignments.value = data.assignments
          progress.value = new Map(data.progress.map(entry => [entry.comicId, entry]))
          const ids = new Set(data.categories.map(c => c.id))
          selectedCategories.value = selectedCategories.value.filter(id => ids.has(id))
          ready.value = true
          // 新加入並完成索引的作品隨即註冊進雲端目錄，雲端清單因此涵蓋尚未備份的作品。
          catalog.schedule(data.comics)
        },
        error(cause) { error.value = errorMessage(cause); ready.value = true },
      })
      await restorePermissions()
    } catch (cause) {
      error.value = errorMessage(cause)
      ready.value = true
      initialized = false
    }
  }

  function runNext() {
    if (worker || !queue.length) return
    const job = queue.shift()!
    activeId = job.comicId
    const finish = (message?: string) => {
      if (message) indexErrors.value[job.comicId] = message
      indexing.value.delete(job.comicId)
      worker?.terminate()
      worker = undefined
      activeId = undefined
      runNext()
    }
    try {
      worker = new Worker(new URL('../workers/indexer.worker.ts', import.meta.url), { type: 'module' })
      worker.onmessage = (event: MessageEvent<IndexResult>) => finish(event.data.error)
      worker.onerror = (event) => { event.preventDefault(); finish('索引中斷，請重整書庫後再試。') }
      worker.onmessageerror = () => finish('無法傳遞索引資料，請重整書庫後再試。')
      worker.postMessage(job)
    } catch (cause) { finish(errorMessage(cause)) }
  }

  function enqueue(comic: Comic, root: FileSystemDirectoryHandle) {
    if (indexing.value.has(comic.id)) return
    delete indexErrors.value[comic.id]
    indexing.value.add(comic.id)
    queue.push({ comicId: comic.id, root, relPath: comic.relPath, kind: comic.kind })
    runNext()
  }

  /** 確認作品的來源還在。解析不到就是找不到檔案。 */
  async function probe(library: Pick<Library, 'handle'>, comic: Comic): Promise<void> {
    if (comic.kind === 'archive') { await resolveComicArchive(library.handle, comic.relPath); return }
    const directory = await resolveComicDirectory(library.handle, comic.relPath)
    // 直接匯入的來源沒有子目錄可解析，改以列舉第一個項目確認資料夾還在。
    if (!comic.relPath) await directory.entries().next()
  }

  /** 尚未索引，或還缺一張可用的封面縮圖時才需要跑 Worker。 */
  async function needsWork(comic: Comic): Promise<boolean> {
    if (!comic.indexed) return true
    const thumbnail = await db.coverThumbnails.get(comic.id)
    return !thumbnail || thumbnail.page !== comic.coverPage || thumbnail.width < COVER_THUMBNAIL_WIDTH
  }

  function unavailable(cause: unknown): boolean {
    return cause instanceof DOMException && ['NotFoundError', 'TypeMismatchError'].includes(cause.name)
  }

  /** 掃描已授權來源底下的作品，標記解析不到的那些。未授權來源的作品維持原狀。 */
  async function refresh() {
    if (refreshing.value || !libraries.value.length) return
    refreshing.value = true
    error.value = ''
    const gone = new Set(missing.value)
    try {
      for (const library of libraries.value) {
        if (!authorizedIds.value.has(library.id)) continue
        const owned = await db.comics.where('libraryId').equals(library.id).toArray()
        for (const comic of owned) {
          try {
            await probe(library, comic)
            gone.delete(comic.id)
            if (await needsWork(comic)) enqueue(comic, library.handle)
          } catch (cause) {
            if (unavailable(cause)) gone.add(comic.id)
            else throw cause
          }
        }
      }
      missing.value = gone
    } catch (cause) {
      if (cause instanceof DOMException && ['NotAllowedError', 'SecurityError'].includes(cause.name)) {
        authorizedIds.value = new Set()
      }
      error.value = errorMessage(cause)
    } finally { refreshing.value = false }
  }

  async function pickDirectory(): Promise<FileSystemDirectoryHandle | undefined> {
    // 必須在點擊事件的同步呼叫堆疊中啟動，前方不可插入 await。
    try {
      return await window.showDirectoryPicker({ mode: 'read' })
    } catch (cause) {
      // 使用者取消選擇器不是錯誤，不顯示任何訊息。
      if (!(cause instanceof DOMException && cause.name === 'AbortError')) throw cause
      return undefined
    }
  }

  async function registerSource(handle: FileSystemDirectoryHandle, kind: Library['kind']): Promise<Library | 'duplicate'> {
    const existing = await findSameSource(libraries.value, handle)
    if (existing) return 'duplicate'
    const entry: Library = { id: crypto.randomUUID(), name: handle.name, kind, handle, addedAt: Date.now() }
    await db.libraries.add(entry)
    await loadLibraries()
    authorizedIds.value = new Set([...authorizedIds.value, entry.id])
    return entry
  }

  /** 加入一個根資料夾來源。回傳新來源；選到既有來源時回傳 'duplicate'。 */
  async function addRoot(): Promise<Library | 'duplicate' | undefined> {
    if (busy.value || !supported) return undefined
    busy.value = true
    error.value = ''
    try {
      const handle = await pickDirectory()
      if (!handle) return undefined
      return await registerSource(handle, 'root')
    } catch (cause) {
      error.value = errorMessage(cause)
      return undefined
    } finally { busy.value = false }
  }

  /** 直接把一個資料夾本身匯入為一本作品。 */
  async function importComicFolder(): Promise<Comic | 'duplicate' | 'empty' | undefined> {
    if (busy.value || !supported) return undefined
    busy.value = true
    error.value = ''
    try {
      const handle = await pickDirectory()
      if (!handle) return undefined
      if (await findSameSource(libraries.value, handle)) return 'duplicate'
      const files = await imageFileNames(handle)
      if (!files.length) return 'empty'
      const source = await registerSource(handle, 'comic')
      if (source === 'duplicate') return 'duplicate'
      // 相對路徑是空的，標題改由來源名稱（就是使用者選的那個資料夾名）推導。
      const [comic] = await addComics(source, [{ relPath: '', kind: 'folder', pageCount: files.length }], source.name)
      if (comic) enqueue(comic, handle)
      return comic
    } catch (cause) {
      error.value = errorMessage(cause)
      return undefined
    } finally { busy.value = false }
  }

  /** 為單一來源請求權限。必須在使用者手勢的同步呼叫堆疊中呼叫。 */
  async function authorize(libraryId: string) {
    const library = libraryForComic(libraries.value, libraryId)
    if (!library || busy.value) return
    busy.value = true
    error.value = ''
    try {
      const permission = await library.handle.requestPermission({ mode: 'read' })
      if (permission === 'granted') {
        authorizedIds.value = new Set([...authorizedIds.value, library.id])
        await refresh()
      } else {
        error.value = `尚未取得「${library.name}」的資料夾權限。你可以再按一次它的開啟按鈕。`
      }
    } catch (cause) {
      authorizedIds.value = new Set([...authorizedIds.value].filter(id => id !== library.id))
      error.value = errorMessage(cause)
    } finally { busy.value = false }
  }

  async function candidates(libraryId: string): Promise<CandidateScan> {
    const library = libraryForComic(libraries.value, libraryId)
    if (!library || library.kind !== 'root') throw new Error('請先選擇一個根資料夾來源。')
    if (!authorizedIds.value.has(library.id)) throw new Error(`請先開啟「${library.name}」。`)
    const added = await db.comics.where('libraryId').equals(library.id).toArray()
    return listCandidates(library.handle, new Set(added.map(c => c.relPath)))
  }

  async function addSelected(libraryId: string, relPaths: string[]) {
    const library = libraryForComic(libraries.value, libraryId)
    if (!library || !authorizedIds.value.has(library.id)) throw new Error('請先開啟這個來源。')
    const { candidates: available } = await candidates(libraryId)
    const wanted = new Set(relPaths)
    const selected = available.filter(c => wanted.has(c.relPath) && !c.added && c.pageCount > 0 && !c.error)
    const added = await addComics(library, selected.map(({ relPath, kind, pageCount }) => ({ relPath, kind, pageCount })))
    for (const comic of added) enqueue(comic, library.handle)
  }

  async function deleteComic(id: string) {
    // 先刪資料；Worker 的提交交易會再檢查作品是否仍存在。
    const comic = await db.comics.get(id)
    await removeComic(id)
    // kind 為 comic 的來源只服務那一本，作品刪掉後來源也沒有存在的意義。
    if (comic) {
      const source = libraryForComic(libraries.value, comic.libraryId)
      if (source?.kind === 'comic') {
        await db.libraries.delete(source.id)
        await loadLibraries()
      }
    }
    for (let i = queue.length - 1; i >= 0; i--) if (queue[i].comicId === id) queue.splice(i, 1)
    if (activeId === id) { worker?.terminate(); worker = undefined; activeId = undefined }
    indexing.value.delete(id)
    missing.value.delete(id)
    delete indexErrors.value[id]
    runNext()
  }

  /**
   * 來源重新指向的第一步：選新資料夾並在它底下逐本探測。
   *
   * 必須在使用者手勢的同步呼叫堆疊內呼叫——選擇器在第一個 await 之前開啟。探測只解析目錄
   * 結構不讀圖片，與 refresh 同量級。選到其他來源的資料夾回 'duplicate'；選到自己目前的
   * 資料夾視為重新指向同一處（handle 物件可能已換新），照常預覽。取消選擇器回 undefined。
   */
  async function previewRelocation(libraryId: string): Promise<RelocationPreview | 'duplicate' | undefined> {
    const library = libraryForComic(libraries.value, libraryId)
    if (!library || busy.value || !supported) return undefined
    busy.value = true
    try {
      const handle = await pickDirectory()
      if (!handle) return undefined
      const existing = await findSameSource(libraries.value.filter(item => item.id !== libraryId), handle)
      if (existing) return 'duplicate'
      const owned = await db.comics.where('libraryId').equals(libraryId).toArray()
      const missingTitles: string[] = []
      for (const comic of owned) {
        try { await probe({ handle }, comic) }
        catch (cause) {
          if (!unavailable(cause)) throw cause
          missingTitles.push(comic.title)
        }
      }
      return { libraryId, handle, found: owned.length - missingTitles.length, total: owned.length, missingTitles }
    } finally { busy.value = false }
  }

  /** 來源重新指向的第二步：寫入 handle、標記已授權、重整。識別碼與底下作品全部不動。 */
  async function relocateSource(preview: RelocationPreview): Promise<void> {
    await relocateLibrary(preview.libraryId, preview.handle)
    await loadLibraries()
    authorizedIds.value = new Set([...authorizedIds.value, preview.libraryId])
    await refresh()
  }

  /**
   * 作品重新指向：改鍵交易之後收拾記憶體裡以舊識別碼記的狀態，再探測一次新位置。
   *
   * 目標來源必須已授權——探測需要讀它；正在索引的作品不能改鍵，Worker 的提交交易會以
   * 舊識別碼寫入。改鍵後 liveQuery 會發出新清單，選取的修剪由那裡處理。
   */
  async function relinkComic(id: string, target: { libraryId: string; relPath: string }): Promise<Comic> {
    const library = libraryForComic(libraries.value, target.libraryId)
    if (!library) throw new Error('找不到目標來源。')
    if (!authorizedIds.value.has(library.id)) throw new Error(`請先開啟「${library.name}」。`)
    if (indexing.value.has(id)) throw new Error('這本作品正在建立索引，請等它完成再更新位置。')
    const relinked = await relinkComicRow(id, target)
    for (let i = queue.length - 1; i >= 0; i--) if (queue[i].comicId === id) queue.splice(i, 1)
    indexing.value.delete(id)
    delete indexErrors.value[id]
    const gone = new Set(missing.value)
    gone.delete(id)
    try {
      await probe(library, relinked)
      if (await needsWork(relinked)) enqueue(relinked, library.handle)
    } catch (cause) {
      if (!unavailable(cause)) throw cause
      gone.add(relinked.id)
    }
    missing.value = gone
    return relinked
  }

  /** 移除來源與它底下的全部作品。硬碟檔案不受影響。 */
  async function removeSource(libraryId: string): Promise<number> {
    const owned = await db.comics.where('libraryId').equals(libraryId).toArray()
    const ids = new Set(owned.map(comic => comic.id))
    for (let i = queue.length - 1; i >= 0; i--) if (ids.has(queue[i].comicId)) queue.splice(i, 1)
    if (activeId && ids.has(activeId)) { worker?.terminate(); worker = undefined; activeId = undefined }
    const removed = await removeLibrary(libraryId)
    for (const id of ids) {
      indexing.value.delete(id)
      missing.value.delete(id)
      delete indexErrors.value[id]
    }
    authorizedIds.value = new Set([...authorizedIds.value].filter(id => id !== libraryId))
    await loadLibraries()
    runNext()
    return removed
  }

  onScopeDispose(() => { subscription?.unsubscribe(); worker?.terminate() })
  return { libraries, roots, comics, categories, assignments, progress, ready, authorized, authorizedIds,
    hasSources, unauthorizedSources, busy, refreshing, error, missing, indexing, indexErrors, query,
    selectedCategories, supported, filteredComics, comicsForCategories, isAuthorized, sourceOf,
    comicsInSource, initialize, addRoot, importComicFolder, authorize, refresh, candidates, addSelected, deleteComic, removeSource,
    previewRelocation, relocateSource, relinkComic,
    selectionMode, selection, selectedCount, selectedInFilter, selectedComics,
    enterSelectionMode, exitSelectionMode, toggleSelected, selectAllFiltered, clearSelected, dropFromSelection }
})
